import { PanelOrientation } from "@inrange/building-manager-api-client/models-site";
import { SiteCalculationsContext, useContextTS } from "@inrange/shared-components";
import { Tooltip } from "@inrange/theme-components";
import { Icons } from "@inrange/theme-components/icons";
import { Button, Col, Form, Row } from "react-bootstrap";
import CollapsibleCard from "../CollapsibleCard";

const PanelOrientationsEditor = ({
  orientations,
  genIdx,
  esysPanelCount,
  onChange,
}: {
  orientations: PanelOrientation[];
  genIdx: string | number;
  esysPanelCount: number;
  onChange: (orientations: PanelOrientation[]) => void;
}) => {
  const { errors } = useContextTS(SiteCalculationsContext);
  const totalPanels = esysPanelCount;

  const updateOrientation = (
    index: number,
    field: keyof PanelOrientation,
    value: string
  ) => {
    const updated = [...orientations];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const addOrientation = () => {
    const usedPanels = orientations.reduce(
      (sum, po) => sum + (Number(po.panelCount) || 0),
      0
    );
    const remaining = Math.max(0, totalPanels - usedPanels);
    onChange([...orientations, { tilt: 0, azimuth: 0, panelCount: remaining }]);
  };

  const removeOrientation = (index: number) => {
    onChange(orientations.filter((_, i) => i !== index));
  };

  const topLevelError = errors[`generation.${genIdx}.panelOrientations`];

  return (
    <CollapsibleCard
      title={`Panel orientations - ${orientations.length} orientation${orientations.length !== 1 ? "s" : ""}`}
      className="mt-2"
      initialOpen={false}
    >
      <p className="text-muted small mb-2">
        Configure tilt, azimuth, and panel count for each distinct roof plane.
        Required for weather actuals ingestion.
      </p>
      {orientations.map((po, index) => (
        <Row key={index} className="mb-2">
          <Col sm={4}>
            <Form.Group className="mb-0">
              {index === 0 && <Form.Label>Tilt (degrees)</Form.Label>}
              <Form.Control
                type="text"
                value={po.tilt}
                isInvalid={
                  !!errors[
                    `generation.${genIdx}.panelOrientations.${index}.tilt`
                  ]
                }
                onChange={(e) =>
                  updateOrientation(index, "tilt", e.target.value)
                }
              />
              <Form.Control.Feedback type="invalid">
                {errors[`generation.${genIdx}.panelOrientations.${index}.tilt`]}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col sm={4}>
            <Form.Group className="mb-0">
              {index === 0 && (
                <Form.Label>
                  Azimuth (0° = North){" "}
                  <Tooltip
                    position="left"
                    text={`Geographic convention (0-360°):\n0° = North\n90° = East\n180° = South\n270° = West\n\nTo convert from PVSyst: add 180°`}
                  />
                </Form.Label>
              )}
              <Form.Control
                type="text"
                value={po.azimuth}
                isInvalid={
                  !!errors[
                    `generation.${genIdx}.panelOrientations.${index}.azimuth`
                  ]
                }
                onChange={(e) =>
                  updateOrientation(index, "azimuth", e.target.value)
                }
              />
              <Form.Control.Feedback type="invalid">
                {
                  errors[
                    `generation.${genIdx}.panelOrientations.${index}.azimuth`
                  ]
                }
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col sm={4}>
            <Form.Group className="mb-0" controlId={`panelCount-${index}`}>
              {index === 0 && <Form.Label>Panel count</Form.Label>}
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "8px",
                }}
              >
                <div style={{ flex: 1 }}>
                  <Form.Control
                    type="text"
                    value={po.panelCount}
                    isInvalid={
                      !!errors[
                        `generation.${genIdx}.panelOrientations.${index}.panelCount`
                      ]
                    }
                    onChange={(e) =>
                      updateOrientation(index, "panelCount", e.target.value)
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {
                      errors[
                        `generation.${genIdx}.panelOrientations.${index}.panelCount`
                      ]
                    }
                  </Form.Control.Feedback>
                </div>
                <span style={{ marginLeft: "4px", paddingTop: "4px" }}>
                  <img
                    alt="remove"
                    src={Icons.trashcanRed}
                    style={{
                      width: "20px",
                      height: "20px",
                      cursor: "pointer",
                    }}
                    onClick={() => removeOrientation(index)}
                  />
                </span>
              </div>
            </Form.Group>
          </Col>
        </Row>
      ))}
      <div className="d-flex align-items-center gap-3">
        <Button variant="outline-primary" size="sm" onClick={addOrientation}>
          Add orientation
        </Button>
        {topLevelError && (
          <span className="text-danger small">{topLevelError}</span>
        )}
      </div>
    </CollapsibleCard>
  );
};

export default PanelOrientationsEditor;
